import styled from "@emotion/styled";
import { ExButton, ExTabs, useResizeObserver } from "@energyx-dev/energyx-ui";
import { LoginForm } from "components/auth/LoginForm";
import { DEVICE } from "constants/device";
import { hackleClient } from "hackle/hackleInstance";
import useTranslation from "hooks/useTranslation";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { convertPath } from "utils/utils";

export const INDIVIDUAL_DEFAULT: {
  phoneNumber: string;
  password: string;
  save: boolean;
} = {
  phoneNumber: "",
  password: "",
  save: false,
};

const Login = () => {
  const navigate = useNavigate();
  const device = useResizeObserver();
  const searchParams = new URLSearchParams(location.search);
  const callbackUrl = searchParams.get("callbackUrl");
  const methods = useForm({
    mode: "onTouched",
    defaultValues: {
      ...INDIVIDUAL_DEFAULT,
    },
  });

  const isEnglishPage = useTranslation();

  const [tabIndex, setTabIndex] = useState(0);
  const urlParameter = window.document.referrer;
  const handleOnClickSignUp = () => {
    if (isEnglishPage) {
      navigate(`/signup?callbackUrl=${callbackUrl}&lang=en`);
      return;
    }
    navigate(`/signup${callbackUrl ? `?callbackUrl=${callbackUrl}` : ""}`);
  };

  useEffect(() => {
    hackleClient.track({
      key: "enter_Login",
      properties: {
        SiteName: urlParameter ? convertPath(urlParameter as string) : "",
      },
    });
  }, []);

  return (
    <>
      <FormProvider {...methods}>
        <LoginContainer>
          <TitleBox>
            <span>{isEnglishPage ? "Sign in" : "로그인"}</span>
            {isEnglishPage ? (
              <>
                <span>
                  Get the full-range of EnergyX service {device !== DEVICE.MOBILE ? " " : <br />}
                  with one account.
                </span>
              </>
            ) : (
              <>
                <span>
                  하나의 계정으로 에너지엑스의
                  {device !== DEVICE.MOBILE ? " " : <br />}모든 서비스를 이용해 보세요.
                </span>
              </>
            )}
          </TitleBox>
          <Tabs>
            <div className="tab-box">
              <ExTabs
                tabs={[
                  {
                    name: isEnglishPage ? "Individual" : "일반 회원 로그인",
                    isExistNew: false,
                  },
                  {
                    name: isEnglishPage ? "Business" : "법인 회원 로그인",
                    isExistNew: false,
                  },
                ]}
                variant="underline"
                size="small"
                contentsIndex={tabIndex}
                setContentsIndex={setTabIndex}
                gap="0px"
              />
            </div>
            <LoginForm tabIndex={tabIndex} urlParameter={urlParameter} />
            <Helper>
              <span>
                {isEnglishPage ? "It only takes a minute to join." : "1분 만에 회원이 되어보세요."}
              </span>
              <ExButton
                variant="contents"
                label={isEnglishPage ? "Join" : "회원가입"}
                size="large"
                labelColor={isEnglishPage ? "#2F2F30" : ""}
                onClick={handleOnClickSignUp}
              />
            </Helper>
          </Tabs>
        </LoginContainer>
      </FormProvider>
    </>
  );
};

const LoginContainer = styled.section`
  width: 100%;
  margin: 0 auto 0;
  display: flex;
  padding: 80px 0;
  flex-direction: column;
  align-items: center;
  gap: 40px;
  ${({ theme }) => theme.breakpoints.down("DESKTOP")} {
    width: 448px;
    gap: 24px;
    padding: 24px 0 40px;
  }
  ${({ theme }) => theme.breakpoints.down("TABLET")} {
    width: 100%;
    padding: 24px 16px 40px 16px;
  }
`;

const TitleBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 16px;
  width: 683px;

  ${({ theme }) => theme.breakpoints.down("DESKTOP")} {
    width: 100%;
  }

  span:first-of-type {
    ${({ theme }) => theme.font.H2}

    ${({ theme }) => theme.breakpoints.down("DESKTOP")} {
      ${({ theme }) => theme.font.Body3_h}
    }
  }

  span:last-of-type {
    color: ${({ theme }) => theme.color.mono[60]};
    ${({ theme }) => theme.font.Body1}

    ${({ theme }) => theme.breakpoints.down("DESKTOP")} {
      ${({ theme }) => theme.font.Subtitle2}
    }
  }
`;

const Tabs = styled.article`
  display: flex;
  flex-direction: column;
  width: 448px;
  gap: 24px;
  > div:first-of-type {
    display: flex;
    flex-direction: column;
  }
  ${({ theme }) => theme.breakpoints.down("DESKTOP")} {
    width: 100%;
  }
  .tab-box {
    border-bottom: ${({ theme }) => `1px solid ${theme.color.mono[10]}`};
  }
`;

const Helper = styled.div`
  display: flex;
  justify-content: center;
  gap: 32px;

  > span {
    flex: 1;
    align-self: center;
    color: ${({ theme }) => theme.color.mono[80]};
    ${({ theme }) => theme.font.Subtitle2};
    padding: 0 8px;
  }
`;

export default Login;
